import  { useState, useEffect, useRef} from "react";
import "../../../../../../css/CashflowModeller2/Charts/ChartTabs.css";
import ButtonBig from "../../Inputs/ButtonBig";
import { useCfmContext } from "../../../Context/CfmContext";
import PensionGraph from "../Graphs/PensionGraph";
import StackedPensionGraph from "../Graphs/StackedPensionGraph";
import StackedInvestmentGraph from "../Graphs/StackedInvestmentGraph";
import CombinedSurplusDeficitGraph from "../Graphs/CombinedSurplusDeficitGraph";
import CombinedIncomeExpenditureGraph from "../Graphs/CombinedIncomeExpenditure";
import InvestmentGraph from "../Graphs/InvestmentGraph";
import IncomeExpenditureGraph from "../Graphs/IncomeExpenditureGraph";
import SurplusDeficitGraph from "../Graphs/SurplusDeficitGraph";
import "../../../../../../css/CashflowModeller2/ToolTips/TooltipBox.css";
import TooltipBox from "../../Tooltips/TooltipBox";
import RadioButton from "../../Buttons/RadioButton";
import NotesDialogBox from "../../DialogBoxes/NotesDialogBox";
import { ReactComponent as NotesIcon } from "../../../../../../assets/icons/notes-icon.svg";
import { ReactComponent as PrintIcon } from "../../../../../../assets/icons/print.svg";

/**
 * ChartTabs component renders a set of interactive chart tabs for visualizing financial data
 * such as Income & Expenditure, Surplus & Deficit, Pension, and Investment for a selected person.
 *
 * Features:
 * - Allows selection of a person from the provided details.
 * - Displays summary information and tooltips for each chart tab at the person's retirement age.
 * - Supports editing notes for the selected person.
 * - Dynamically calculates and displays financial metrics (income, surplus, pension, investment) at retirement.
 * - Provides color-coded legends and tooltips for better data visualization.
 *
 * State:
 * - `calculatedIncome`: The total retirement income after tax for the selected person.
 * - `activeTab`: The currently active chart tab.
 * - `showTooltip`: Whether to show the tooltip for the hovered chart tab.
 * - `hoveredData`: Data to display in tooltips for each chart tab.
 * - `selectedPerson`: The currently selected person.
 * - `isDialogOpen`: Whether the notes dialog is open.
 *
 * Context:
 * - Uses `useCfmContext` to access global state for financial data and person details.
 *
 * Effects:
 * - Updates selected person and calculated income when relevant state changes.
 * - Updates tooltip data when selected person, retirement age, or state changes.
 *
 * @component
 * @returns {JSX.Element} The rendered ChartTabs component.
 */
const ChartTabs = () => {
  const { state } = useCfmContext();
  const [calculatedIncome, setCalculatedIncome] = useState("£0.00");
  const [activeTab, setActiveTab] = useState("Income & Expenditure");
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredData, setHoveredData] = useState({
    incomeExpenditure: null,
    surplus: null,
    pension: null,
    investment: null,
  });
  const [selectedPerson, setSelectedPerson] = useState(
    state.details?.[0]?.name || ""
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false); // ← dialog state
  const selectedPersonDetails = state.details.find(
    (person) =>
      person.name?.trim().toLowerCase() === selectedPerson?.trim().toLowerCase()
  );
  const retirementAge = selectedPersonDetails?.retirementAge || "";
  const entry = state.incomeExpenditureResults?.find(
    (e) => e.name === selectedPerson
  );
  const surplus = entry?.surplusAtRetirement || "£0.00";
  const [isCombinedSectionVisible, setIsCombinedSectionVisible] =
    useState(false);

  /**
   * Calculates the total pension fund and related financial figures for a specific person at their retirement age.
   *
   * Iterates through the pension results in the application state, filters for the specified person,
   * and sums up the relevant values (fund value, ad hoc contributions/withdrawals, income, TFC, phased TFC)
   * for pension funds that match the retirement age.
   *
   * @param {string} personName - The name of the person whose pension fund totals are to be calculated.
   * @returns {Object} An object containing the following properties:
   *   @property {number} fund - The total pension fund val at retirement.
   *   @property {number} adhocContribution - The total ad hoc contributions at retirement.
   *   @property {number} adhocWithdrawal - The total ad hoc withdrawals at retirement.
   *   @property {number} income - The total taxed drawdown (income) at retirement.
   *   @property {number} tfc - The total tax-free cash (TFC) withdrawal at retirement.
   *   @property {number} phased - The total phased TFC at retirement.
   */
  const getTotalPensionFundAtRetirement = (personName) => {
    const results = state.pensionResults || [];
    const personPensions = results.filter((entry) => entry.name === personName);

    let totalFund = 0;
    let totalAdHocContributions = 0;
    let totalAdHocWithdrawals = 0;
    let totalIncome = 0;
    let totalTfc = 0;
    let totalPhase = 0;

    personPensions.forEach((pension) => {
      const pensionFunds = pension.pensionFunds || [];

      pensionFunds.forEach((fund) => {
        const age = parseInt(fund.age);
        if (age !== parseInt(retirementAge)) return;

        const parseValue = (v) =>
          v ? parseFloat(v.toString().replace(/[^0-9.-]+/g, "")) || 0 : 0;

        totalFund += parseValue(fund.fundValue);
        totalAdHocContributions += parseValue(fund.adhocContribution);
        totalAdHocWithdrawals += parseValue(fund.adhocWithdrawal);
        totalIncome += parseValue(fund.taxedDrawdown);
        totalTfc += parseValue(fund.tflsWithdrawal);
        totalPhase += parseValue(fund.phasedTfc);
      });
    });

    return {
      fund: totalFund,
      adhocContribution: totalAdHocContributions,
      adhocWithdrawal: totalAdHocWithdrawals,
      income: totalIncome,
      tfc: totalTfc,
      phased: totalPhase,
    };
  };

  /**
   * Calculates the total investment fund and related financial figures for a given person at their retirement age.
   *
   * @param {string} personName - The name of the person whose investment data is to be aggregated.
   * @returns {Object} An object containing the following properties:
   *   @property {number} fund - The total fund value at retirement age.
   *   @property {number} contribution - The total contributions made up to retirement age.
   *   @property {number} withdrawal - The total regular withdrawals made up to retirement age.
   *   @property {number} adhocContribution - The total ad hoc contributions made up to retirement age.
   *   @property {number} adhocWithdrawal - The total ad hoc withdrawals made up to retirement age.
   */
  const getTotalInvestmentFundAtRetirement = (personName) => {
    const results = state.investmentResults || [];
    const personInvestments = results.filter(
      (entry) => entry.name === personName
    );

    let totalFund = 0;
    let totalContributions = 0;
    let totalWithdrawals = 0;
    let totalAdHocContributions = 0;
    let totalAdHocWithdrawals = 0;

    const parseValue = (v) =>
      v ? parseFloat(v.toString().replace(/[^0-9.-]+/g, "")) || 0 : 0;

    personInvestments.forEach((investment) => {
      const fundEntries = investment.investmentFunds || [];

      fundEntries.forEach((fund) => {
        const age = parseInt(fund.age);
        if (age !== parseInt(retirementAge)) return;

        totalFund += parseValue(fund.fundValue);
        totalContributions += parseValue(fund.contribution);
        totalWithdrawals += parseValue(fund.regularWithdrawals);
        totalAdHocContributions += parseValue(fund.adhocInvestment);
        totalAdHocWithdrawals += parseValue(fund.adhocWithdrawals);
      });
    });

    return {
      fund: totalFund,
      contribution: totalContributions,
      withdrawal: totalWithdrawals,
      adhocContribution: totalAdHocContributions,
      adhocWithdrawal: totalAdHocWithdrawals,
    };
  };

  /**
   * Retrieves and formats the income and expenditure details for a specific person at a given age.
   *
   * @param {string} personName - The name of the person whose data is to be retrieved.
   * @param {number|string} age - The age at which to retrieve the income and expenditure data.
   * @returns {Object|null} An object containing formatted income and expenditure fields for the specified person and age,
   *                        or null if no data is found.
   * @returns {string} return.salary - Formatted salary amount.
   * @returns {string} return.pensionRegularIncome - Formatted regular pension income.
   * @returns {string} return.investmentRegularIncome - Formatted regular investment income.
   * @returns {string} return.btlRental - Formatted buy-to-let rental income.
   * @returns {string} return.dbPension - Formatted defined benefit pension income.
   * @returns {string} return.statePension - Formatted state pension income.
   * @returns {string} return.otherIncome - Formatted other income.
   * @returns {string} return.adhocIncome - Formatted ad-hoc income.
   * @returns {string} return.pensionAdhocContribution - Formatted ad-hoc pension contribution.
   * @returns {string} return.pensionAdhocWithdrawal - Formatted ad-hoc pension withdrawal.
   * @returns {string} return.investmentAdhocContribution - Formatted ad-hoc investment contribution.
   * @returns {string} return.investmentAdhocWithdrawal - Formatted ad-hoc investment withdrawal.
   * @returns {string} return.taxFreeCash - Formatted tax-free cash amount.
   * @returns {string} return.totalExpenditure - Formatted total expenditure.
   */
  const getIncomeExpenditureAtAge = (personName, age) => {
    const results = state.incomeExpenditureResults || [];
    const personData = results.find((entry) => entry.name === personName);
    const incomeData = personData?.incomeExpenditure || [];

    const row = incomeData.find((r) => +r.age === +age);
    if (!row) return null;

    const format = (v) =>
      `£${(
        parseFloat((v ?? "0").toString().replace(/[^0-9.-]+/g, "")) || 0
      ).toLocaleString()}`;

    return {
      salary: format(row.salary),
      pensionRegularIncome: format(row.pensionRegularIncome),
      investmentRegularIncome: format(row.investmentRegularIncome),
      btlRental: format(row.btlRental),
      dbPension: format(row.dbPension),
      statePension: format(row.statePension),
      otherIncome: format(row.otherIncome),
      adhocIncome: format(row.adhocIncome),
      pensionAdhocContribution: format(row.pensionAdhocContribution),
      pensionAdhocWithdrawal: format(row.pensionAdhocWithdrawal),
      investmentAdhocContribution: format(row.investmentAdhocContribution),
      investmentAdhocWithdrawal: format(row.investmentAdhocWithdrawal),
      taxFreeCash: format(row.taxFreeCash),
      totalExpenditure: format(row.totalExpenditure),
    };
  };

  // useEffect(() => {
  //   selectedPerson = selectedPerson || state.details?.[0]?.name || "";
  // }, []);
  const pensionTotals = getTotalPensionFundAtRetirement(selectedPerson);
  const investmentTotals = getTotalInvestmentFundAtRetirement(selectedPerson);

  useEffect(() => {
    if (state.details?.[0]?.name && !selectedPerson) {
      setSelectedPerson(state.details[0].name);
    }
  }, [state.details]);

  useEffect(() => {
    if (Array.isArray(state.calculatedIncome)) {
      const entry = state.calculatedIncome.find((item) =>
        item.name?.startsWith(selectedPerson)
      );
      setCalculatedIncome(
        (entry?.value ? entry.value : "£0.00")
          .toString()
          .replace(/[^0-9.-]+/g, "")
      );
    } else {
      setCalculatedIncome("0.00");
    }
  }, [state.calculatedIncome, selectedPerson]);
  const incomeIconRef = useRef(null);

  useEffect(() => {
    if (!selectedPerson || !retirementAge) return;

    const incomeExpenditure = getIncomeExpenditureAtAge(
      selectedPerson,
      retirementAge
    );

    const surplusEntry = state.incomeExpenditureResults?.find(
      (e) => e.name === selectedPerson
    );
    const surplus =
      surplusEntry?.surplusAtRetirement?.toString().replace(/[^0-9.-]+/g, "") ??
      "0";

    setHoveredData({
      incomeExpenditure,
      surplus,
      pension,
      investment,
    });
  }, [selectedPerson, retirementAge, state]);

  const colorMap = {
    salary: "#ED64A6",
    pensionRegularIncome: "#4299E1",
    investmentRegularIncome: "#9F7AEA",
    btlRental: "#F6AD55",
    dbPension: "#38B2AC",
    statePension: "#4C51BF",
    otherIncome: "#667EEA",
    adhocIncome: "#F56565",
    pensionAdhocWithdrawal: "#4FD1C5",
    pensionAdhocContribution: "#00AD3F",
    investmentAdhocContribution: "#B794F4",
    investmentAdhocWithdrawal: "#FC8181",
    taxFreeCash: "#48BB78",
    totalExpenditure: "#E53E3E",
  };

  const surplusValue =
    parseFloat(surplus?.toString().replace(/[^0-9.-]+/g, "")) || 0;

  const pension = getTotalPensionFundAtRetirement(selectedPerson);
  const investment = getTotalInvestmentFundAtRetirement(selectedPerson);
  const incomeApiRef = useRef(null);
  const surplusApiRef = useRef(null);
  const pensionApiRef = useRef(null);
  const investmentApiRef = useRef(null);
  const stackedPensionApiRef = useRef(null);
  const stackedInvestmentApiRef = useRef(null);
  const combinedIncomeApiRef = useRef(null);
  const combinedSurplusApiRef = useRef(null);

  const apiRefByTab = {
    "Income & Expenditure":
      selectedPerson === "Combined Charts"
        ? combinedIncomeApiRef
        : incomeApiRef,
    "Surplus & Deficit":
      selectedPerson === "Combined Charts"
        ? combinedSurplusApiRef
        : surplusApiRef,
    Pension:
      selectedPerson === "Combined Charts"
        ? stackedPensionApiRef
        : pensionApiRef,
    Investment:
      selectedPerson === "Combined Charts"
        ? stackedInvestmentApiRef
        : investmentApiRef,
    // "Combined Income": combinedIncomeApiRef,
    // "Combined Surplus": combinedSurplusApiRef,
  };

  const handlePrintPDF = () => {
    const apiRef = apiRefByTab[activeTab];
    if (apiRef?.current?.exportAsPrint) {
      const fileName = `${selectedPerson} - ${activeTab}`.replace(
        /[\/\\?%*:|"<>]/g,
        "-"
      );
      apiRef.current.exportAsPrint({ fileName });
    }
  };

  const handleDownloadPNG = () => {
    const apiRef = apiRefByTab[activeTab];
    if (apiRef?.current?.exportAsImage) {
      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${now.getFullYear()}`; // DD-MM-YYYY

      const fileName = `${selectedPerson} - ${activeTab} - ${dateStr}`.replace(
        /[\/\\?%*:|"<>]/g,
        "-"
      );

      apiRef.current.exportAsImage({ type: "image/png", fileName });
    }
  };

  return (
    <div className="cfm-chart-container">
      <div className="Chart-Actions-Row">
        <div className="radiobutton-Container">
          <RadioButton
            options={state.details || []}
            selected={selectedPerson}
            onChange={setSelectedPerson}
          />

          {state.details.length > 1 && (
            <div
              className="radiobutton-Container"
              onClick={() => setSelectedPerson("Combined Charts")}
            >
              <label className="radiooption">
                <input
                  className="radiobutton"
                  type="radio"
                  name="person"
                  value={"Combined Charts"}
                  checked={selectedPerson === "Combined Charts"}
                  onChange={() => setSelectedPerson("Combined Charts")}
                />
                <span className="Custom-radiobutton"></span>
              </label>
              <div className="Input">Combined</div>
            </div>
          )}
        </div>
        {state.details.length > 0 && (
          <div className="custom-notes-container">
            <div
              className="custom-notes Content-Small"
              onClick={handleDownloadPNG}
            >
              Export
              <PrintIcon />
            </div>
            {selectedPerson !== "Combined Charts" && (
              <div
                className="custom-notes Content-Small"
                onClick={() => setIsDialogOpen(true)}
              >
                Edit Notes
                <NotesIcon />
              </div>
            )}
          </div>
        )}
        {isDialogOpen && (
          <div className="dialog-overlay">
            <NotesDialogBox
              onClose={() => setIsDialogOpen(false)}
              selectedPerson={selectedPerson}
              activeTab={activeTab}
            />
          </div>
        )}
      </div>
      <div className="Graph-Container">
        {activeTab === "Pension" &&
          (selectedPerson === "Combined Charts" ? (
            <StackedPensionGraph
              animated={true}
              isPrint={false}
              apiRef={stackedPensionApiRef}
            />
          ) : (
            <PensionGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={pensionApiRef}
            />
          ))}
        {activeTab === "Investment" &&
          (selectedPerson === "Combined Charts" ? (
            <StackedInvestmentGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={stackedInvestmentApiRef}
            />
          ) : (
            <InvestmentGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={investmentApiRef}
            />
          ))}
        {activeTab === "Income & Expenditure" &&
          (selectedPerson === "Combined Charts" ? (
            <CombinedIncomeExpenditureGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={combinedIncomeApiRef}
            />
          ) : (
            <IncomeExpenditureGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={incomeApiRef}
            />
          ))}
        {activeTab === "Surplus & Deficit" &&
          (selectedPerson === "Combined Charts" ? (
            <CombinedSurplusDeficitGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={combinedSurplusApiRef}
            />
          ) : (
            <SurplusDeficitGraph
              selectedPerson={selectedPerson}
              animated={true}
              isPrint={false}
              apiRef={surplusApiRef}
            />
          ))}

        {activeTab === "Income & Expenditure" && retirementAge && (
          <div className="Content-Small" style={{ marginBottom: "10px" }}>
            At age {retirementAge}, the total retirement income after tax is
            expected to be £
            {Math.max(0, Number(calculatedIncome)).toLocaleString("en-UK", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        )}

        {activeTab === "Surplus & Deficit" && retirementAge && (
          <div className="Content-Small" style={{ marginBottom: "10px" }}>
            {surplusValue >= 0
              ? `At age ${retirementAge}, the surplus at retirement is expected to be £${surplusValue.toLocaleString(
                  "en-UK",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}`
              : `At age ${retirementAge}, the deficit at retirement is expected to be £${Math.abs(
                  surplusValue
                ).toLocaleString("en-UK", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
          </div>
        )}

        {activeTab === "Pension" && retirementAge && (
          <div className="Content-Small" style={{ marginBottom: "10px" }}>
            The total Fund value at Retirement (Age {retirementAge}) will be £
            {Math.max(0, pensionTotals.fund).toLocaleString("en-UK", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        )}

        {activeTab === "Investment" && retirementAge && (
          <div className="Content-Small" style={{ marginBottom: "10px" }}>
            The total Investment Fund at Retirement (Age {retirementAge}) will
            be £
            {Math.max(0, investmentTotals.fund).toLocaleString("en-UK", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        )}

        <div className="Chart-Tab-Container">
          <ButtonBig
            buttonText="Income & Expenditure"
            isActive={activeTab === "Income & Expenditure"}
            onClick={() => setActiveTab("Income & Expenditure")}
            showTooltip={selectedPerson !== "Combined Charts" ? true : false}
            iconRef={incomeIconRef}
            onIconHover={() => {
              setHoveredData(
                getIncomeExpenditureAtAge(selectedPerson, retirementAge)
              );
              setShowTooltip(true);
            }}
            onIconLeave={() => setShowTooltip(false)}
            children={
              hoveredData.incomeExpenditure && (
                <div className="tooltip-hover-content">
                  <div className="Content-SemiBold">
                    Retirement Age ({retirementAge})
                  </div>
                  <div>
                    {Object.entries(colorMap).map(([key, color]) => {
                      const value = hoveredData.incomeExpenditure?.[key];
                      if (!value || value === "£0.00" || value === "£0")
                        return null;

                      const label =
                        key == "btlRental"
                          ? "BTL Rental"
                          : key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase());

                      return (
                        <div key={key} className="hover-row">
                          {/* <div className="hover-row-header"> */}
                          {/* <div
                        className="legend"
                        style={{ backgroundColor: color }}
                      /> */}
                          <div className="Content-Small">{label}</div>
                          {/* </div> */}
                          <div className="Content-Small">{value}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            }
          />
          <ButtonBig
            buttonText="Surplus & Deficit"
            isActive={activeTab === "Surplus & Deficit"}
            onClick={() => setActiveTab("Surplus & Deficit")}
            showTooltip={selectedPerson !== "Combined Charts" ? true : false}
            children={
              hoveredData?.surplus && (
                <div className="tooltip-hover-content">
                  <div className="Content-SemiBold">
                    Retirement Age ({retirementAge})
                  </div>
                  <div>
                    <div className="hover-row">
                      <div className="hover-row-header">
                        {/* <div
                      className="legend"
                      style={{
                        backgroundColor:
                          surplusValue > 0
                            ? surplusLegendMap.surplus
                            : surplusLegendMap.deficit,
                      }}
                    /> */}
                        <div className="Content-Small">
                          {surplusValue > 0 ? "Surplus" : "Deficit"}
                        </div>
                      </div>
                      <div className="Content-Small">
                        £{Math.abs(Number(surplusValue)).toLocaleString()}
                      </div>
                    </div>

                    <div className="hover-row">
                      <div className="hover-row-header">
                        {/* <div
                      className="legend"
                      style={{
                        backgroundColor: surplusLegendMap.totalExpenditure,
                      }}
                    /> */}
                        <div className="Content-Small">Total Expenditure</div>
                      </div>
                      <div className="Content-Small">
                        {hoveredData?.incomeExpenditure?.totalExpenditure ??
                          "£0.00"}
                      </div>
                    </div>

                    <div className="hover-row">
                      <div className="hover-row-header">
                        {/* <div
                      className="legend"
                      style={{
                        backgroundColor: surplusLegendMap.totalIncomeAfterTax,
                      }}
                    /> */}
                        <div className="Content-Small">
                          Total Income After Tax
                        </div>
                      </div>
                      <div className="Content-Small">
                        £{Number(calculatedIncome).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )
            }
          />
          <ButtonBig
            buttonText="Pension"
            isActive={activeTab === "Pension"}
            onClick={() => setActiveTab("Pension")}
            showTooltip={selectedPerson !== "Combined Charts" ? true : false}
            children={
              <div>
                {[
                  { key: "fund", label: "Total Fund" },
                  { key: "tfc", label: "Tax-Free Cash (TFC)" },
                  { key: "phased", label: "Phased TFC" },
                  { key: "income", label: "Taxed Drawdown Income" },
                  { key: "adhocContribution", label: "Adhoc Contribution" },
                  { key: "adhocWithdrawal", label: "Adhoc Withdrawal" },
                ]
                  .filter(
                    ({ key }) =>
                      hoveredData?.pension?.[key] &&
                      hoveredData.pension[key] !== 0
                  )
                  .map(({ key, label }, index) => (
                    <div key={key} className="tooltip-hover-content">
                      {index === 0 && (
                        <div className="Content-SemiBold">
                          Retirement Age ({retirementAge})
                        </div>
                      )}
                      <div className="hover-row">
                        <div className="hover-row-header">
                          <div className="Content-Small">{label}</div>
                        </div>
                        <div className="Content-Small">
                          £{Number(hoveredData.pension[key]).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            }
          />

          <ButtonBig
            buttonText="Investment"
            isActive={activeTab === "Investment"}
            onClick={() => setActiveTab("Investment")}
            showTooltip={selectedPerson !== "Combined Charts" ? true : false}
            children={
              <div>
                {[
                  { key: "fund", label: "Total Fund" },
                  { key: "contribution", label: "Regular Contribution" },
                  { key: "withdrawal", label: "Regular Withdrawal" },
                  { key: "adhocContribution", label: "Adhoc Contribution" },
                  { key: "adhocWithdrawal", label: "Adhoc Withdrawal" },
                ]
                  .filter(
                    ({ key }) =>
                      hoveredData?.investment?.[key] &&
                      hoveredData.investment[key] !== 0
                  )
                  .map(({ key, label }, index) => (
                    <div key={key} className="tooltip-hover-content">
                      {index === 0 && (
                        <div className="Content-SemiBold">
                          Retirement Age ({retirementAge})
                        </div>
                      )}
                      <div key={key} className="hover-row">
                        <div className="hover-row-header">
                          {/* <div
                      className="legend"
                      style={{ backgroundColor: investmentLegendMap[key] }}
                    /> */}
                          <div className="Content-Small">{label}</div>
                        </div>
                        <div className="Content-Small">
                          £
                          {Number(hoveredData.investment[key]).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            }
          />
        </div>

        {showTooltip && hoveredData && (
          <TooltipBox anchorRef={incomeIconRef}></TooltipBox>
        )}
      </div>
    </div>
  );
};

export default ChartTabs;
